import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserBotApi } from './api';
import type { AddUserTasksInlineRequest, CreateUserBotInlineRequest } from './types';

export const userBotKeys = {
  all: ['events'] as const,
  list: (orgId: string, includeSop?: boolean) => ['events', orgId, includeSop] as const,
};

export const useListUserBots = (orgId: string, includeSop?: boolean) => {
  return useQuery({
    queryKey: userBotKeys.list(orgId, includeSop),
    queryFn: () => UserBotApi.listUserBots(orgId, includeSop),
    enabled: !!orgId,
  });
};

export const useCreateUserBot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, name, file }: { orgId: string; name: string; file: File }) =>
      UserBotApi.createUserBot(orgId, name, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userBotKeys.all });
    },
  });
};

export const useDeleteUserBot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, botCode }: { orgId: string; botCode: string }) =>
      UserBotApi.deleteUserBot(orgId, botCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userBotKeys.all });
    },
  });
};

export const useAddUserTasks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, botCode, file }: { orgId: string; botCode: string; file: File }) =>
      UserBotApi.addUserTasks(orgId, botCode, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userBotKeys.all });
    },
  });
};

export const useAddUserBotTasksInline = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, botCode, data }: { orgId: string; botCode: string; data: AddUserTasksInlineRequest }) =>
      UserBotApi.addUserBotTasksInline(orgId, botCode, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userBotKeys.all });
    },
  });
};

export const useParseUserTasks = () => {
  return useMutation({
    mutationFn: ({ orgId, file }: { orgId: string; file: File }) =>
      UserBotApi.parseUserTasks(orgId, file),
  });
};

export const useCreateUserBotInline = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: CreateUserBotInlineRequest }) =>
      UserBotApi.createUserBotInline(orgId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userBotKeys.all });
    },
  });
};
